// The `config` blob inside one `clayfarm.seasons` row (PLAN-01 Stream D2).
//
// Hand-written validator, no dependency: the shape is small, the game bundle
// already refuses to carry a schema library, and a season config that fails to
// parse must fail LOUDLY here rather than halfway through a scored season.
//
// The validator is the one place that knows what a season config may contain, and
// it is deliberately shared by both ends — the edge function that writes a
// season and the client that reads one — so a config that writes always reads.

export interface HatcheryShipmentEntry {
  breed: string;
  eggs: number;
  /** Eggits (1 Eggit = 1 USD, REFOCUS §11b). */
  price: number;
}

export interface HatcheryShipment {
  /** Season week, 1-based. One order a week (REFOCUS §12b). */
  week: number;
  stock: HatcheryShipmentEntry[];
}

/**
 * Straight-run chick prices.
 *
 * `common` is the price of any breed `byBreed` does not list, because REFOCUS §5
 * wants one figure for a layer breed and a second for the Silkie, not a list the
 * owner has to keep complete every time a breed is added.
 */
export interface SeasonChickPrices {
  common: number;
  /** Breed id → straight-run price, overriding `common`. */
  byBreed: Record<string, number>;
}

export interface SeasonBagSize {
  size: 'small' | 'big';
  /** Days of feed the bag holds. */
  days: number;
  /** Eggits. */
  cost: number;
}

export interface SeasonConfig {
  version: 1;
  /** Item id → Eggits. Every key must be a real item id in items.json. */
  prices: Record<string, number>;
  /** Straight-run chick prices; the buy screen's per-breed rows (REFOCUS §5). */
  chickPrices: SeasonChickPrices;
  /** Eggits the opening basket may spend on chicks (REFOCUS §5). */
  chickBudget: number;
  /** Sexed chicks cost this multiple of the straight-run price. */
  sexedChickMultiplier: number;
  /** Share of sexed chicks that are hens, the buy screen's "~90% hens". */
  sexedHenRate: number;
  /** The feed bags the store sells this season. */
  bagSizes: SeasonBagSize[];
  /** REFOCUS §11b: coop included + 60 Eggits. */
  startEggits: number;
  normalWeeklyAllowance: number;
  /** REFOCUS §11a: Backyard 4-6, Balcony 2-4. The player's call. */
  flockSize: { backyard: [number, number]; balcony: [number, number] };
  hatcheryShipments: HatcheryShipment[];
  /** REFOCUS §11c draft: 12 h. */
  veteranWarningCooldownHours: number;
}

export type SeasonConfigResult =
  | { ok: true; config: SeasonConfig }
  | { ok: false; errors: string[] };

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

const isFiniteNumber = (v: unknown): v is number =>
  typeof v === 'number' && Number.isFinite(v);

/** A whole, positive number: no negative eggs, no fractional week 1.5. */
function isPositiveInt(v: unknown): v is number {
  return isFiniteNumber(v) && Number.isInteger(v) && v > 0;
}

function isNonNegative(v: unknown): v is number {
  return isFiniteNumber(v) && v >= 0;
}

/** A price multiplier: 1 or more, because sexed chicks never cost less. */
function isMultiplier(v: unknown): v is number {
  return isFiniteNumber(v) && v >= 1;
}

/** A share of a whole, in (0, 1]: a percentage that is not zero or over 100. */
function isFraction(v: unknown): v is number {
  return isFiniteNumber(v) && v > 0 && v <= 1;
}

/** `[min, max]` of two numbers with min <= max — the flock-size pairs. */
function isAscendingPair(v: unknown): v is [number, number] {
  return (
    Array.isArray(v) &&
    v.length === 2 &&
    isFiniteNumber(v[0]) &&
    isFiniteNumber(v[1]) &&
    v[0] <= v[1]
  );
}

export interface ValidateOptions {
  /**
   * Known item ids. When given, every price key must be one of them, which is
   * what stops a typo like `feed_layr` from shipping a season where buying layer
   * feed silently costs nothing. The client passes the ids from items.json; the
   * edge function passes the same list.
   */
  itemIds?: ReadonlySet<string>;
  /**
   * Breed ids the game offers. When given, every `chickPrices.byBreed` key must
   * be one of them, which stops a season that prices a breed under a typo'd id
   * and silently charges the `common` price instead.
   */
  breedIds?: ReadonlySet<string>;
}

/**
 * Validates an unknown value into a SeasonConfig, collecting every problem
 * rather than stopping at the first, so a rejected season can be fixed in one
 * round trip from the admin panel.
 */
export function validateSeasonConfig(
  value: unknown,
  options: ValidateOptions = {},
): SeasonConfigResult {
  const errors: string[] = [];
  if (!isRecord(value)) {
    return { ok: false, errors: ['config must be an object'] };
  }

  if (value.version !== 1) errors.push(`version must be 1, got ${JSON.stringify(value.version)}`);

  const prices: Record<string, number> = {};
  if (!isRecord(value.prices)) {
    errors.push('prices must be an object');
  } else {
    for (const [id, price] of Object.entries(value.prices)) {
      if (!isNonNegative(price)) {
        errors.push(`prices.${id} must be a number >= 0, got ${JSON.stringify(price)}`);
        continue;
      }
      if (options.itemIds && !options.itemIds.has(id)) {
        errors.push(`prices.${id} is not an item id in items.json`);
        continue;
      }
      prices[id] = price;
    }
  }

  let chickPrices: SeasonConfig['chickPrices'] | null = null;
  const rawChickPrices = value.chickPrices;
  if (!isRecord(rawChickPrices)) {
    errors.push('chickPrices must be an object');
  } else if (!isNonNegative(rawChickPrices.common)) {
    errors.push(`chickPrices.common must be a number >= 0, got ${JSON.stringify(rawChickPrices.common)}`);
  } else if (!isRecord(rawChickPrices.byBreed)) {
    errors.push('chickPrices.byBreed must be an object');
  } else {
    const byBreed: Record<string, number> = {};
    let byBreedOk = true;
    for (const [breed, price] of Object.entries(rawChickPrices.byBreed)) {
      if (!isNonNegative(price)) {
        errors.push(`chickPrices.byBreed.${breed} must be a number >= 0, got ${JSON.stringify(price)}`);
        byBreedOk = false;
        continue;
      }
      if (options.breedIds && !options.breedIds.has(breed)) {
        errors.push(`chickPrices.byBreed.${breed} is not a breed the game offers`);
        byBreedOk = false;
        continue;
      }
      byBreed[breed] = price;
    }
    if (byBreedOk) chickPrices = { common: rawChickPrices.common, byBreed };
  }

  if (!isNonNegative(value.chickBudget)) {
    errors.push(`chickBudget must be a number >= 0, got ${JSON.stringify(value.chickBudget)}`);
  }
  if (!isMultiplier(value.sexedChickMultiplier)) {
    errors.push(
      `sexedChickMultiplier must be a number >= 1, got ${JSON.stringify(value.sexedChickMultiplier)}`,
    );
  }
  if (!isFraction(value.sexedHenRate)) {
    errors.push(`sexedHenRate must be a number > 0 and <= 1, got ${JSON.stringify(value.sexedHenRate)}`);
  }

  const bagSizes: SeasonBagSize[] = [];
  if (!Array.isArray(value.bagSizes)) {
    errors.push('bagSizes must be an array');
  } else if (value.bagSizes.length === 0) {
    errors.push('bagSizes must be a non-empty array');
  } else {
    const seenSizes = new Set<string>();
    value.bagSizes.forEach((bag, i) => {
      if (!isRecord(bag)) {
        errors.push(`bagSizes[${i}] must be an object`);
        return;
      }
      if (bag.size !== 'small' && bag.size !== 'big') {
        errors.push(`bagSizes[${i}].size must be "small" or "big"`);
        return;
      }
      if (seenSizes.has(bag.size)) {
        errors.push(`bagSizes[${i}].size ${bag.size} is listed twice`);
        return;
      }
      seenSizes.add(bag.size);
      if (!isPositiveInt(bag.days)) {
        errors.push(`bagSizes[${i}].days must be a whole number >= 1`);
        return;
      }
      if (!isNonNegative(bag.cost)) {
        errors.push(`bagSizes[${i}].cost must be a number >= 0`);
        return;
      }
      bagSizes.push({
        size: bag.size as SeasonBagSize['size'],
        days: bag.days as number,
        cost: bag.cost as number,
      });
    });
  }

  if (!isNonNegative(value.startEggits)) {
    errors.push(`startEggits must be a number >= 0, got ${JSON.stringify(value.startEggits)}`);
  }
  if (!isNonNegative(value.normalWeeklyAllowance)) {
    errors.push(
      `normalWeeklyAllowance must be a number >= 0, got ${JSON.stringify(value.normalWeeklyAllowance)}`,
    );
  }
  if (!isNonNegative(value.veteranWarningCooldownHours)) {
    errors.push(
      `veteranWarningCooldownHours must be a number >= 0, got ${JSON.stringify(
        value.veteranWarningCooldownHours,
      )}`,
    );
  }

  let flockSize: SeasonConfig['flockSize'] | null = null;
  if (!isRecord(value.flockSize)) {
    errors.push('flockSize must be an object');
  } else if (!isAscendingPair(value.flockSize.backyard) || !isAscendingPair(value.flockSize.balcony)) {
    errors.push('flockSize.backyard and flockSize.balcony must each be [min, max] with min <= max');
  } else {
    flockSize = {
      backyard: value.flockSize.backyard as [number, number],
      balcony: value.flockSize.balcony as [number, number],
    };
  }

  const hatcheryShipments: HatcheryShipment[] = [];
  if (!Array.isArray(value.hatcheryShipments)) {
    errors.push('hatcheryShipments must be an array');
  } else {
    const seenWeeks = new Set<number>();
    value.hatcheryShipments.forEach((shipment, i) => {
      if (!isRecord(shipment)) {
        errors.push(`hatcheryShipments[${i}] must be an object`);
        return;
      }
      if (!isPositiveInt(shipment.week)) {
        errors.push(`hatcheryShipments[${i}].week must be a whole number >= 1`);
        return;
      }
      if (seenWeeks.has(shipment.week)) {
        errors.push(`hatcheryShipments[${i}].week ${shipment.week} is listed twice`);
        return;
      }
      seenWeeks.add(shipment.week);

      if (!Array.isArray(shipment.stock) || shipment.stock.length === 0) {
        errors.push(`hatcheryShipments[${i}].stock must be a non-empty array`);
        return;
      }
      const stock: HatcheryShipmentEntry[] = [];
      let entryOk = true;
      shipment.stock.forEach((entry, j) => {
        if (!isRecord(entry)) {
          errors.push(`hatcheryShipments[${i}].stock[${j}] must be an object`);
          entryOk = false;
          return;
        }
        if (typeof entry.breed !== 'string' || entry.breed.length === 0) {
          errors.push(`hatcheryShipments[${i}].stock[${j}].breed must be a non-empty string`);
          entryOk = false;
        }
        if (!isPositiveInt(entry.eggs)) {
          errors.push(`hatcheryShipments[${i}].stock[${j}].eggs must be a whole number >= 1`);
          entryOk = false;
        }
        if (!isNonNegative(entry.price)) {
          errors.push(`hatcheryShipments[${i}].stock[${j}].price must be a number >= 0`);
          entryOk = false;
        }
        if (entryOk) {
          stock.push({ breed: entry.breed as string, eggs: entry.eggs as number, price: entry.price as number });
        }
      });
      if (entryOk) hatcheryShipments.push({ week: shipment.week as number, stock });
    });
  }

  if (errors.length > 0) return { ok: false, errors };

  return {
    ok: true,
    config: {
      version: 1,
      prices,
      chickPrices: chickPrices as SeasonConfig['chickPrices'],
      chickBudget: value.chickBudget as number,
      sexedChickMultiplier: value.sexedChickMultiplier as number,
      sexedHenRate: value.sexedHenRate as number,
      bagSizes,
      startEggits: value.startEggits as number,
      normalWeeklyAllowance: value.normalWeeklyAllowance as number,
      flockSize: flockSize as SeasonConfig['flockSize'],
      hatcheryShipments,
      veteranWarningCooldownHours: value.veteranWarningCooldownHours as number,
    },
  };
}
